import * as Yup from 'yup';

import {
  contactsSchema,
  contentSchema,
  imageSchema,
  locationSchema,
  seoValidationSchema,
} from 'utils/yup';
import {
  mapAddress,
  mapContacts,
  mapContent,
  mapContentToForm,
  mapGallery,
  mapImage, mapImageForSeo,
  mapSelect,
  mapValuesToAddress,
  mapValuesToSelect
} from "utils/mappings";
import { prepareContent } from 'components/form/Content/utils';
import { Statuses, StatusesEnum } from 'constants/status';
import { IHotelsItem, IHotelsDraftItem } from 'store/slices/hotels/interfaces';
import { LangEnum, LangEnumKeys } from 'constants/lang';

export const mapComfortGroups = values => {
  return values.map(group => ({
    id: group.value,
    comfort: group.comfort ? group.comfort : null,
  }));
};

export const mapComfortGroupsToForm = (comfortGroups, comfortGroupsData) => {
  return comfortGroups.map(group => {
    const name =
      comfortGroupsData.find(groupData => groupData.id === group.id).name ||
      null;
    return {
      comfort: group.comfort || '',
      value: group.id,
      children: name,
      label: name,
    };
  });
};

function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

export const mapValuesToForm = values => {
  return {
    ...values,
    address: values.address ? mapValuesToAddress(values.address) : {},
    arrivalTime: values.arrivalTime,
    category: values.categoryData
      ? mapValuesToSelect(values.categoryData)
      : null,
    comfortGroups:
      values.comfortGroups && values.comfortGroupsData
        ? mapComfortGroupsToForm(values.comfortGroups, values.comfortGroupsData)
        : [],
    content: prepareContent(mapContentToForm(values.content || [])),
    departureTime: values.departureTime,
    features: values.featuresData ? mapValuesToSelect(values.featuresData) : [],
    hotelComforts: values.hotelComfortsData
      ? mapValuesToSelect(values.hotelComfortsData)
      : [],
    intro: values?.intro || '',
    lang: values.lang || LangEnum.ru,
    metaDescription: values.metaDescription || '',
    metaKeywords: values.metaKeywords || '',
    metaTitle: values.metaTitle || '',
    name: values.name || '',
    ogDescription: values.ogDescription || '',
    ogTitle: values.ogTitle || '',
    rating: values.rating ? Number(values.rating) : null,
    shortDescription: stripHtml(values.shortDescription) || '',
    starRating: values.starRating ? values.starRating : 'Не задано',
    status:
      values.status === StatusesEnum.DRAFT
        ? StatusesEnum.PUBLISHED
        : values.status,
    ogType: values.ogType || null,
    ogUrl: values.ogUrl || null,
    ogSiteName: values.ogSiteName || null,
  };
};

export const validationSchema = Yup.object()
  .shape({
    address: locationSchema,
    arrivalTime: Yup.mixed(),
    category: Yup.mixed().required(),
    comfortGroups: Yup.array()
      .max(10, 'Выберите не более ${max} удобств')
      .of(
        Yup.object().shape({
          comfort: Yup.string()
            .trim()
            .min(1, 'Введите от 1 до 800 символов')
            .max(800, 'Введите от 1 до 800 символов')
            .required(),
        })
      ),
    contacts: contactsSchema,
    content: contentSchema,
    departureTime: Yup.mixed(),
    externalUrl: Yup.string().url(),
    features: Yup.array().max(10, 'Выберите не более ${max} особенностей'),
    gallery: Yup.array().of(imageSchema),
    image: imageSchema,
    intro: Yup.string()
      .notRequired()
      .trim()
      .max(700, 'Введите не более 600 символов'),
    name: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 255 символов')
      .max(255, 'Введите от 1 до 255 символов')
      .required(),
    hotelComforts: Yup.array().max(
      10,
      'Выберите не более ${max} удобств в отеле'
    ),
    price: Yup.mixed(),
    rating: Yup.mixed(),
    shortDescription: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    status: Yup.mixed().required(),
    starRating: Yup.mixed(),
    ogTitle: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    ogDescription: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    metaTitle: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    metaDescription: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    ogUrl: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
    ogSiteName: Yup.string()
      .trim()
      .min(1, 'Введите от 1 до 300 символов')
      .max(300, 'Введите от 1 до 300 символов')
      .required(),
  })
  .concat(seoValidationSchema);

export const mapValues = (values): IHotelsItem => {
  return {
    address: mapAddress(values.address),
    arrivalTime: values.arrivalTime ? values.arrivalTime : null,
    category: mapSelect(values.category) as number,
    comfortGroups: values.comfortGroups.length
      ? mapComfortGroups(values.comfortGroups)
      : [],
    contacts: mapContacts(values.contacts),
    content: mapContent(values.content),
    departureTime: values.departureTime ? values.departureTime : null,
    externalUrl: values.externalUrl || '',
    features: values.features.length
      ? (mapSelect(values.features) as number[])
      : [],
    gallery: mapGallery(values.gallery),
    heroImage: null,
    hotelComforts: values.hotelComforts
      ? (mapSelect(values.hotelComforts) as number[])
      : [],
    image: mapImage(values.image),
    intro: values?.intro?.trim() || '',
    lang: mapSelect(values.lang) as LangEnumKeys,
    metaDescription: values.metaDescription || null,
    metaKeywords: values.metaKeywords || null,
    metaTitle: values.metaTitle || null,
    name: values.name.trim(),
    ogDescription: values.ogDescription || null,
    ogTitle: values.ogTitle || null,
    price: values.price,
    rating: values.rating ? Number(values.rating) : null,
    shortDescription: values.shortDescription.trim(),
    sortPriority: values.sortPriority || null,
    sortPriorityMainPage: values.sortPriorityMainPage || null,
    starRating: values.starRating !== 'Не задано' ? values.starRating : null,
    status: mapSelect(values.status) as Statuses,
    ogImage: values.ogImage ? mapImageForSeo(mapImage(values.ogImage)) : null,
    ogType: values.ogType || null,
    ogUrl: values.ogUrl || null,
    ogSiteName: values.ogSiteName || null,
  };
};

export const mapValuesToDraft = (values): IHotelsDraftItem => {
  return {
    address:
      values.address?.city || values.address?.region
        ? mapAddress(values.address)
        : null,
    arrivalTime: values.arrivalTime ? values.arrivalTime : null,
    category: values.category ? (mapSelect(values.category) as number) : null,
    comfortGroups: values.comfortGroups?.length
      ? mapComfortGroups(values.comfortGroups)
      : [],
    contacts:
      values.contacts?.length > 0 &&
      values.contacts.every(contact => contact.value !== '')
        ? values.contacts
        : [],
    content: values.content?.length ? mapContent(values.content) : [],
    departureTime: values.departureTime ? values.departureTime : null,
    externalUrl: values.externalUrl || '',
    features: values.features?.length
      ? (mapSelect(values.features) as number[])
      : null,
    gallery: values.gallery?.length ? mapGallery(values.gallery) : [],
    hotelComforts: values.hotelComforts?.length
      ? (mapSelect(values.hotelComforts) as number[])
      : null,
    heroImage: null,
    image: values.image ? mapImage(values.image) : null,
    intro: values?.intro?.trim() || '',
    lang: mapSelect(values.lang) as LangEnumKeys,
    metaDescription: values.metaDescription || null,
    metaKeywords: values.metaKeywords || null,
    metaTitle: values.metaTitle || null,
    name: values.name?.trim() || null,
    ogDescription: values.ogDescription || null,
    ogTitle: values.ogTitle || null,
    price: values.price || null,
    rating: values.rating ? Number(values.rating) : null,
    shortDescription: values.shortDescription?.trim() || null,
    starRating: values.starRating !== 'Не задано' ? values.starRating : null,
    ogImage: values.ogImage ? mapImageForSeo(mapImage(values.ogImage)) : null,
    ogType: values.ogType || null,
    ogUrl: values.ogUrl || null,
    ogSiteName: values.ogSiteName || null,
  };
};
